/* @flow */
/* eslint-env es6,browser */

import { getEvaluatedDialup } from './utils/browserutil';
import adconfig from './adconfig';
import usersync from './usersync';
import { logError, logInfo } from './utils/logger';
import {
  extend,
  isObject,
  unique,
  isArray,
  isUndefined,
  shuffle,
  intersect
} from './utils/coreutils';
import {
  allSettledTimedPromise,
  getTimeoutPromise
} from './utils/promiseutils';

// import all the bidders
import a9v2 from './bidders/a9v2bid';
import conversant from './bidders/conversant';
import criteo from './bidders/criteo';
import pubmatic from './bidders/pubmatic';
import rubicon from './bidders/rubicon';
import adtech from './bidders/adtechbid';
import index from './bidders/indexbid';
import sovrn from './bidders/sovrnbid';
import { breal, appnexus } from './bidders/brealbid';
import openx from './bidders/openxbid';

/**
 * @module bidmanager
 * @description Wrapper to invoke and handle all header bidders
 * @requires module:utils
 * @requires module:adconfig
 * @requires module:usersync
 * @requires module:coreutils
 * @requires module:promiseutils
 */

/**
 * @class
 * @description Bid manager class
 */
class _bidManager {
  /*
  - Initialize all supported header bidder object and add to bidders array
  bidders[KEY] - KEY should match the same one in OAS x61 config
  - During each query, only the required one from this object will be added
  to activeBidders array and used
  - Each bidder object should comply with the common public interface (Refer A9 bidder)
  */
  _bidders: {
    [string]: hbPartnerBase
  };

  _activeBidders: Array<hbPartnerBase>;

  _isDialup: boolean;

  _site: string;

  _defConfig: hbBiddersData;

  _config: hbBiddersData;

  _firstLoad: boolean;

  constructor() {
    this._bidders = {};
    this._defConfig = {
      bidders: [],
      bidders_dialup: [],
      biddersOverride: [],
      shuffleBidders: false,
      timeout: 3000, // default timeout for all bidders put together
      userSync: {},
      granularity: 'dense'
    };
    this._config = this._defConfig;
    this._activeBidders = [];
    this._isDialup = false;
    this._site = '';
    this._firstLoad = true;
  }

  /**
   * Register all bidders with bid manager
   *
   * @private
   * @returns {undefined}
   *
   */
  _registerAllBidders() {
    [
      a9v2,
      conversant,
      criteo,
      pubmatic,
      rubicon,
      adtech,
      index,
      sovrn,
      breal,
      appnexus,
      openx
    ].forEach(bidder => this._addBidder(bidder.getName(), bidder));
  }

  /**
   * Initialize the object
   *
   * @param {module:globalParams.globalParams} g - globalParams object
   * @returns {undefined}
   */
  init(g: globalParam) {
    this._isDialup = getEvaluatedDialup(g.isDialup);
    this._site = g.phInfo.site;
    // load all bidders
    this._registerAllBidders();
  }

  /**
   * Add Bidder
   *
   * @external _bidbase
   * @param {string} name - Biddler name
   * @param {_bidbase} bidder - Bidder Object
   * @returns {undefined}
   */
  _addBidder(name: string, bidder: hbPartnerBase) {
    this._bidders[name] = bidder;
  }

  /**
   * get appropriate bidders array based in dialup flag
   *
   * @access private
   * @returns {Array<string>} - List of bidders based on dial-up check
   */
  _getBidders(): Array<string> {
    if (this._config.biddersOverride.length) {
      return this._config.biddersOverride;
    }
    if (this._isDialup && this._config.bidders_dialup.length) {
      return this._config.bidders_dialup;
    }
    return this._config.bidders;
  }

  /**
   * This function should be called after we get x61 config
   * - Stores the config locally
   * - Pick active bidders based on config.bidders
   * - Configure each active bidders with bidder specific config
   *
   * @external hbConfig
   * @param {object.<string, hbConfig>} oConfig - config for all bidders from adconfig
   * @returns {undefined}
   */
  updateConfig(oConfig: hbBiddersData) {
    oConfig = oConfig || {};
    this._config = extend(true, {}, this._defConfig, oConfig);
    usersync.updateSettings(this._config.userSync);

    // empty active bidder object and build again as per updated config
    this._activeBidders.length = 0;
    this._activeBidders = this._getBidders()
      .filter(v =>
        // select only the bidder who has valid handler with us
        isObject(this._bidders[v])
      )
      .map(v => {
        // Update bidder object and store it as active bidder
        const bidObj = this._bidders[v];
        const bidConfig = this._config[v] || {};
        bidObj.updateConfig(bidConfig);
        return bidObj;
      });
  }

  /**
   * Get list of header bid positions.
   * This gets bid positions supported by each/active bidders, concat
   * and return unique.
   *
   * @param {string} pName - page name
   * @returns {Array<string>} - List of HB enabled positions
   */
  getBidPos(pName: string): Array<string> {
    pName = pName || '';
    if (!pName.length) {
      return [];
    }
    return unique(
      this._activeBidders
        .map(o =>
          // get bid positions supported by the bidder
          o.getBidPositions(pName)
        )
        .reduce((a, b) => a.concat(b), [])
    );
  }

  /**
   * @global
   * @typedef hbPosData
   * @property {string} info=<E>
   * @property {object} _QP - bid value(cpm) are populated here
   * @property {object} _CV - partner specific pos id
   */
  /**
   * Prepare default/normalized response for each bid position
   * When we don't get any response from bidder/no bidding available
   * this will be the data returned
   *
   * @param {Array<string>} posList - ad positions
   * @returns {object.<string, hbPosData>} hbPartnerData
   * @access private
   */
  _getDefResponse(posList: Array<string>): hbPartnerData {
    const ret = {};
    const _defObj = { info: '<E>', _QP: {}, _CV: {}, allBids: [] };
    posList = posList || [];
    if (!isArray(posList)) {
      return ret;
    }
    posList.forEach(v => {
      ret[v] = {};
      extend(true, ret[v], _defObj);
    });
    return ret;
  }

  /**
   * Prepare bid response for each bid position with zero bid
   * When we don't get any response from bidder/no bidding available
   * this will be the data returned
   *
   * @param {Array<string>} posList - ad positions
   * @param {object} partner - partner object
   * @returns {object.<string, hbPosData>} hbPartnerData
   * @access private
   */
  _initBidResponse(
    posList: Array<string>,
    partner: hbPartnerBase
  ): hbPartnerData {
    const ret = {};
    const _defObj = {
      info: '',
      _QP: {},
      _CV: {},
      winningBid: {
        hb_pb: '0',
        hb_bidder: partner.getName(),
        hb_adid: ''
      }
    };
    posList = posList || [];
    if (!isArray(posList)) {
      return ret;
    }
    posList.forEach(v => {
      ret[v] = {};
      extend(true, ret[v], _defObj);
      ret[v]._QP[partner.getQPKey(v)] = 0;
      ret[v].info = `${partner.getLineId(v)}|0`;
    });
    return ret;
  }

  /**
   * a9v2 partner gives bids in ecoded formats that has to decoded to CPM
   *
   * @typedef {object} hb_data
   * @param {hb_data} bid - Encoded bid
   * @returns {number} - Decoded CPM from bucket map
   */
  _getDecodedBidFromBidder(bid: hb_data): number {
    if (!bid.hb_bidder.length) {
      return 0;
    }
    const bidder: hbPartnerBase = this._bidders[bid.hb_bidder];
    return parseFloat(bidder.getBidFromBucketMap(bid.hb_pb));
  }

  /**
   * Get all bid values
   *
   * @param {Array<hb_data>} bids - Bids from all partners
   * @returns {Array<object>} - Normalized bids from all
   */
  _getAllBids(bids: Array<hb_data>): Array<qparams> {
    return bids
      .filter(bid => bid.hb_adid.length > 0 && bid.hb_pb !== '0')
      .map(bid => {
        const { hb_pb, hb_adid, hb_bidder } = bid;
        const granularPrice = this._getGranularityPrice(hb_pb);
        const o = {};
        o[`hb_pb_${hb_bidder}`] = granularPrice;
        o[`hb_adid_${hb_bidder}`] = hb_adid;
        return o;
      });
  }

  /**
   *
   * @param {Array<hb_data>} bids - header bidding data for a given positions
   * @returns {hb_data} - winning bid data
   */
  _getWinningBid(bids: Array<hb_data>): hb_data {
    const defaultWinningBid: hb_data = {
      hb_pb: '0',
      hb_bidder: '',
      hb_adid: ''
    };
    return bids.reduce((maximum, currentValue) => {
      const decodedCurrentBid = this._getDecodedBidFromBidder(currentValue);
      const decodedMaxBid = this._getDecodedBidFromBidder(maximum);
      return decodedMaxBid < decodedCurrentBid ? currentValue : maximum;
    }, defaultWinningBid);
  }

  /**
   * Calculates the final cpm value based on the passed bucket
   *
   * @param {number} cpm - winning cpm
   * @param {object} bucket - Bucket in which the cpm fits
   * @param {number} precision - precision
   * @param {number} granularityMultiplier - granularity
   * @returns {string} - final granular cpm
   */
  _getCpmTarget(
    cpm: number,
    bucket: Gbucket,
    precision: number = 2,
    granularityMultiplier: number = 1
  ): string {
    if (bucket.min <= 0 && bucket.max <= 0 && bucket.increment <= 0) {
      return String(cpm);
    }
    const increment = bucket.increment * granularityMultiplier;
    const bucketMin = bucket.min * granularityMultiplier;

    // start increments at the bucket min and then add bucket min back to arrive at the correct rounding
    // note - we're padding the values to avoid using decimals in the math prior to flooring
    // this is done as JS can return values slightly below the expected mark which would skew the price bucket target
    //   (eg 4.01 / 0.01 = 400.99999999999994)
    // min precison should be 2 to move decimal place over.
    const pow = 10 ** (precision + 2);
    // prettier-ignore
    const cpmToFloor = ((cpm * pow) - (bucketMin * pow)) / (increment * pow);
    // prettier-ignore
    let cpmTarget = (Math.floor(cpmToFloor) * increment) + bucketMin;
    // force to 10 decimal places to deal with imprecise decimal/binary conversions
    //    (for example 0.1 * 3 = 0.30000000000000004)
    cpmTarget = Number(cpmTarget.toFixed(10));
    return cpmTarget.toFixed(precision);
  }

  /**
   *
   * @param {string} cpm - winning bid
   * @returns {string} - price after applying granularity
   */
  _getGranularityPrice(cpm: string = ''): string {
    let gcpm = '';
    const granularityMultiplier = 1;
    const precision = 2;
    if (!cpm.length) {
      return cpm;
    }
    const ncpm = parseFloat(cpm);
    // Accept winnig bid which is not a number in case of bidders like a9v2 and do not apply granularity
    if (Number.isNaN(ncpm) || ncpm <= 0) {
      return cpm;
    }
    const bucketPools: standardBucketPools = {
      dense: {
        buckets: [
          {
            min: 0,
            max: 3,
            increment: 0.01
          },
          {
            min: 3,
            max: 8,
            increment: 0.05
          },
          {
            min: 8,
            max: 20,
            increment: 0.5
          }
        ]
      },
      low: {
        buckets: [
          {
            min: 0,
            max: 5,
            increment: 0.5
          }
        ]
      },
      medium: {
        buckets: [
          {
            min: 0,
            max: 20,
            increment: 0.1
          }
        ]
      },
      high: {
        buckets: [
          {
            min: 0,
            max: 20,
            increment: 0.01
          }
        ]
      },
      auto: {
        buckets: [
          {
            min: 0,
            max: 5,
            increment: 0.05
          },
          {
            min: 5,
            max: 10,
            increment: 0.1
          },
          {
            min: 10,
            max: 20,
            increment: 0.5
          }
        ]
      }
    };
    const configBucketPool: bucketPool = bucketPools[this._config.granularity];
    const cap = configBucketPool.buckets.reduce(
      (prev, curr) => {
        // Gets the bucket with maximum 'max' property
        if (prev.max > curr.max) {
          return prev;
        }
        return curr;
      },
      {
        max: 0
      }
    );
    if (ncpm > cap.max * granularityMultiplier) {
      gcpm = (cap.max * granularityMultiplier).toFixed(precision);
      return gcpm;
    }

    const selectedBucket = configBucketPool.buckets.find(
      bucket =>
        ncpm <= bucket.max * granularityMultiplier &&
        ncpm >= bucket.min * granularityMultiplier
    ) || { min: 0, max: 0, increment: 0 };

    gcpm = this._getCpmTarget(
      ncpm,
      selectedBucket,
      precision,
      granularityMultiplier
    );
    return gcpm;
  }

  /**
   * This performs the actual header bidding. Hence bidding needs to be done async.
   * the response here is promise. Caller needs to wait for the response and
   * handle success/failure via then method.
   * Return object will be like {'<POS>': {},'<POS2>':{}}
   * Each position value will contain extra Query Params & custom vars (Designed based on A9
   * bidding and may change in future)
   *
   * @param {Array<string>} posList - ad positions
   * @param {string} pName - pageName
   * @param {boolean} adRotation - For ad rotation view?
   * @returns {Promise<object.<string, hbPosData>>} hbPartnerData
   */
  getBids(
    posList: Array<string>,
    pName: string,
    adRotation: boolean
  ): Promise<hbPartnerData> {
    const ret = this._getDefResponse(posList);
    const pArray = [];
    const fl = this._firstLoad;
    posList = posList || [];
    pName = pName || '';
    if (!isArray(posList) || !posList.length || !pName.length) {
      return Promise.resolve(ret);
    }

    const startTime = new Date().getTime();

    // send bid request to the bidders who support these positions
    const bidders = this._config.shuffleBidders
      ? shuffle(this._activeBidders)
      : this._activeBidders;
    bidders.forEach(v => {
      // get bid positions supported by this bidder (If NONE, no need to query this bidder)
      const bidArray = intersect(posList, v.getBidPositions(pName));
      if (bidArray.length) {
        // let's wait with timeout for each bidder (so that we can use resolved bids in case of someone times out)
        pArray.push({
          name: v.getName(),
          promise: Promise.race([
            v.getBids(bidArray, this._initBidResponse(bidArray, v)),
            getTimeoutPromise(this._config.timeout, v.getName())
          ])
        });
      }
    });
    if (!pArray.length) {
      return Promise.resolve(ret);
    }

    this._firstLoad = false;

    // Resolve all accumulated promise here
    return allSettledTimedPromise(pArray).then(
      arr => {
        const sValues = arr
          .filter(v => v.state === 'fulfilled')
          .map(v => v.value);
        const fValues = arr
          .filter(v => v.state !== 'fulfilled')
          .map(v => v.reason);

        // Log all failed promise
        if (fValues.length) {
          logError(`Header bidding failed with error ${fValues.join(',')}`);
        }

        // get updated value from bidder response for each position
        posList.forEach(pos => {
          ret[pos] = sValues
            .filter(v => isObject(v[pos])) // filter only bidders who has result for this position
            .map(v => v[pos]) // extract result from bidder
            .reduce((p, c) => {
              // concatenate and send it. Let server decide which bid to use
              if (p.info === '<E>') {
                p.info = '';
              }
              if (p.info.length) {
                p.info += ';';
              }
              p.info += c.info;

              extend(true, p._QP, c._QP);
              extend(true, p._CV, c._CV);
              return p;
            }, ret[pos]);
        });
        posList.forEach(pos => {
          const HB_DATA: Array<hb_data> = sValues
            .filter(v => isObject(v[pos]))
            .map(v => v[pos].winningBid);
          const winner: hb_data = this._getWinningBid(HB_DATA);
          const granularityBid = this._getGranularityPrice(winner.hb_pb);
          winner.hb_pb = granularityBid;
          ret[pos].winningBid = winner;
          ret[pos].allBids = this._getAllBids(HB_DATA);
        });
        const elapsedTime = new Date().getTime() - startTime;
        const perPartnertt = arr.map(v => `${v.name}:${v.tt}`).join('|');
        logInfo(`bids=${posList.map(v => `${v}:${ret[v].info}`).join(',')}&\
tt=${elapsedTime}|${perPartnertt}&\
du=${this._isDialup ? 1 : 0}&\
site=${this._site}&\
pn=${adconfig.getPageName()}&\
fl=${fl ? 1 : 0}&\
ar=${adRotation ? 1 : 0}`);
        // trigger userSync
        const syncBidders = arr.map(v => this._bidders[v.name]);
        usersync.handleUserSync(syncBidders);

        // Log winning bids here for debugging/reporting
        const bidsWonLog = posList
          .filter(v => {
            const w = ret[v].winningBid;
            return w.hb_pb !== '0' && w.hb_bidder !== '';
          })
          .map(v => {
            const { hb_pb, hb_bidder } = ret[v].winningBid;
            const lID = this._bidders[hb_bidder].getLineId(v);
            return [v, [lID, hb_bidder, hb_pb].join('|')].join(':');
          })
          .join(',');
        if (bidsWonLog.length) {
          logInfo(`bidsWon=${bidsWonLog}&site=${this._site}`);
        }
        return ret;
      },
      error => {
        logError(`Header bidding failed: ${error || ''}`);
        return ret;
      }
    );
  }

  /**
   * resize the ad while rendering
   *
   * @param {Document} doc - document object
   * @param {string} pos - ad position
   * @param {posConfigInfo} posInfo - Position information
   * @returns {undefined}
   */
  setrendersize(
    doc: ?Document = null,
    pos: string = '',
    posInfo: posConfigInfo
  ) {
    if (
      doc &&
      pos.length > 0 &&
      doc.defaultView &&
      doc.defaultView.frameElement
    ) {
      const { width, height } = posInfo;
      doc.defaultView.frameElement.width = width;
      doc.defaultView.frameElement.height = height;
    }
  }

  /**
   * render header bidding partner ad
   *
   * @external posConfigInfo
   * @param {window} win - window object
   * @param {Document} doc - document object
   * @param {string} pos - ad position
   * @param {string} partner - partner name
   * @param {string} param - Extra param (may vary for each bidding partner)
   * @param {posConfigInfo} posInfo - Position information
   * @returns {undefined}
   */
  renderAd(
    win: window,
    doc: Document,
    pos: string,
    partner: string,
    param: string,
    posInfo: posConfigInfo
  ) {
    if (!this._bidders[partner] || !win || win === win.top) {
      logError(
        `renderAd failed p=${partner} win:${
          !win || win === win.top ? 'invalid' : 'valid'
        }`
      );
      return;
    }
    let newwin = win;
    // if ad is rendered inside a new iframe created inside our ad iframe
    if ((!pos || !param) && win.parent !== win.top) {
      // eslint-disable-next-line no-loops/no-loops
      do {
        newwin = win.parent;
      } while (newwin.parent !== win.top && !newwin.UOL_POS);
      pos = newwin.UOL_POS;
      param = newwin[this._bidders[partner].getCVar()];
    }
    this._bidders[partner].renderAd(win, doc, pos, param, posInfo);
    doc.close();
    this.setrendersize(doc, pos, posInfo);
    const qp = newwin._QP;
    const pagename = newwin.UOL_PAGENAME || 'default';

    // should we log winning bid? Mostly to exclude Amazon here
    if (!this._bidders[partner]._doReporting()) {
      return;
    }
    let bid = '';
    const ar = newwin.UOL_ADROTATE || 0;
    if (isObject(qp)) {
      // get bid value for send all bid cases
      const bidV = `hb_pb_${partner}`;
      bid = qp[bidV];
      if (isUndefined(bid) || bid.length < 1) {
        const qpKey = this._bidders[partner].getQPKey(pos);
        if (qp.hb_pb) {
          bid = qp.hb_pb;
          // check and log if partner mismatches
          const p = qp.hb_bidder;
          if (p !== partner) {
            logError(
              `BM::renderAd: ${pos} partner mismatch e:${p} r:${partner}`
            );
          }
        } else if (qpKey && qp[qpKey]) {
          bid = qp[qpKey];
        }
      }
    }

    logInfo(
      `renderAd:p=${partner}&pos=${pos}&bid=${bid}&site=${
        this._site
      }&lid=${this._bidders[partner].getLineId(pos)}&pn=${pagename}&ar=${
        ar ? 1 : 0
      }`
    );
  }
}
// export singleton object
export default new _bidManager();
